import { FC, useEffect } from 'react'

import { AssignmentAddonsSection } from './shared/AssignmentAddonsSection.component'
import { AssignmentRendererProps } from './AssignmentRenderer.types'
import {
  DetailSection,
  DetailField,
  DetailCommentBlock,
  UploadedFilesSection,
  RendererQueryState
} from '../common'
import { isNotFoundError } from 'utils/helpers/queryErrorHelpers'
import { logger } from 'utils/logging'
import { useAssignmentDetailDrawerWorkspaceId } from '../AssignmentDetailDrawer.context'
import { useGetAssignmentMeasurementOnSiteDetails } from 'dataQueries/assignmentDetails.query'
import { useTranslation } from 'react-i18next'

const T_PREFIX = 'gallery:assignment_details.measurement'

export const MeasurementOnSiteDetailsRenderer: FC<AssignmentRendererProps> = ({ assignment, order, mainProductId }) => {
  const { t } = useTranslation(['gallery'])
  const workspaceId = useAssignmentDetailDrawerWorkspaceId()
  const { data, isLoading, isError, error, refetch } = useGetAssignmentMeasurementOnSiteDetails(assignment.id, workspaceId)

  useEffect(() => {
    if (!isError || isNotFoundError(error)) return
    logger.error('MeasurementOnSiteDetailsRenderer: failed to load details', error instanceof Error ? error : undefined, {
      context: { component: 'MeasurementOnSiteDetailsRenderer', metadata: { assignmentId: assignment.id, workspaceId } },
    })
  }, [isError, error, assignment.id, workspaceId])

  const additionalAreas = [
    data?.basementAreaToMeasure && t(`${T_PREFIX}.areas.basement`),
    data?.atticAreaToMeasure && t(`${T_PREFIX}.areas.attic`),
  ].filter(Boolean).join('; ')

  // Add-ons (ISSUE-2838) — ops read this drawer to learn an order is Express, so both belong on the detail
  const addOns = [
    data?.expressService && t(`${T_PREFIX}.add_ons.express_service`),
    data?.additionalHouseViews && t(`${T_PREFIX}.add_ons.additional_house_views`),
  ].filter(Boolean).join('; ')

  const reasons = data?.additionalInformation
    ?.map(info => t(`${T_PREFIX}.reason.${info}`))
    .join('; ')

  return (
    <RendererQueryState assignmentId={assignment.id} isLoading={isLoading} isError={isError} onRetry={refetch}>

      <DetailSection title={t(`${T_PREFIX}.property.title`)}>
        <DetailField
          label={t(`${T_PREFIX}.property.property_type`)}
          value={data?.propertyType && t(`${T_PREFIX}.property_type_labels.${data.propertyType}`)}
        />
        <DetailField
          label={t(`${T_PREFIX}.property.surface_area`)}
          value={data?.surfaceArea != null ? `${data.surfaceArea} m²` : undefined}
        />
        <DetailField
          label={t(`${T_PREFIX}.property.number_of_rooms`)}
          value={data?.numberOfRooms}
        />
        <DetailField
          label={t(`${T_PREFIX}.property.number_of_floors`)}
          value={data?.numberOfFloors}
        />
        <DetailField
          label={t(`${T_PREFIX}.property.number_of_residential_units`)}
          value={data?.numberOfResidentialUnits}
        />
        <DetailField
          label={t(`${T_PREFIX}.property.additional_areas`)}
          value={additionalAreas || undefined}
        />
        {/* Hidden by DetailField when neither add-on was bought, so pre-ISSUE-2838 orders render unchanged */}
        <DetailField
          label={t(`${T_PREFIX}.property.add_ons`)}
          value={addOns || undefined}
        />
      </DetailSection>

      <DetailSection title={t(`${T_PREFIX}.additional.title`)}>
        <DetailField
          label={t(`${T_PREFIX}.additional.reason_for_measurement`)}
          value={reasons}
        />
      </DetailSection>

      {!!data?.comment &&
        <DetailSection title={t(`${T_PREFIX}.comments`)}>
          <DetailCommentBlock>{data.comment}</DetailCommentBlock>
        </DetailSection>
      }

      <AssignmentAddonsSection assignment={assignment} order={order} mainProductId={mainProductId} />

      <UploadedFilesSection assignmentId={assignment.id} />

    </RendererQueryState>
  )
}
